import { useState, useEffect } from "react";

import { useHandleUnsuitablePress } from "@/components/shared/business/UnsuitableReason/hooks/useHandleUnsuitablePress";
import { ProcessStatusCode } from "@/types/code.types";
import { useReviewRequiredMutation } from "@api/articleInfo/useArticleInfo";
import { useUnsuitablePressCancelMutation } from "@api/unsuitablePress/unsuitablePress";
import { DRAFT_STATUS_CODE } from "@constants/draftStatusCode.constants";
import {
  checkSuperAdmin,
  useAuthorityType,
} from "@libs/roles/hooks/useAuthorityType";

import UnsuitableCheckboxIcon from "./components/UnsuitableCheckboxIcon/UnsuitableCheckboxIcon";
import { UnsuitableReasonUsageType } from "../UnsuitableReason/types/unsuitableReason.types";

interface Props {
  onModalStateChange?: (isOpen: boolean) => void;
  id: number;
  duplArticleId: number | null;
  articleRejectReason: string | null;
  draftArticleStatusHighest: ProcessStatusCode;
  mcEditorName: string | null;
  aiDone: boolean;
  unsuitableReasonUsageType: UnsuitableReasonUsageType;
  reviewRequiredText?: string;
  reviewIdList?: number[] | null;
}

const ListUnsuitableContainer = ({
  onModalStateChange,
  id,
  articleRejectReason,
  duplArticleId,
  draftArticleStatusHighest,
  mcEditorName,
  aiDone,
  unsuitableReasonUsageType,
  reviewRequiredText = "",
  reviewIdList = null,
}: Props) => {
  const [targetUnsuitablePressId, setTargetUnsuitablePressId] =
    useState<number>(0);
  const authorityType = useAuthorityType();
  const isSuperAdmin = checkSuperAdmin(authorityType);
  const { mutateAsync: turnOffUnsuitable } = useUnsuitablePressCancelMutation(
    unsuitableReasonUsageType,
  );
  const { mutateAsync: updateReviewRequiredStatus } = useReviewRequiredMutation(
    unsuitableReasonUsageType,
  );

  const {
    openModalUnsuitablePress,
    closeModalUnsuitablePress,
    isOpenModalUnsuitablePress,
  } = useHandleUnsuitablePress({ type: unsuitableReasonUsageType });

  useEffect(() => {
    const isModalOpen =
      isOpenModalUnsuitablePress ||
      (targetUnsuitablePressId === id && !articleRejectReason);

    if (onModalStateChange) {
      onModalStateChange(isModalOpen);
    }
  }, [
    isOpenModalUnsuitablePress,
    targetUnsuitablePressId,
    id,
    articleRejectReason,
    onModalStateChange,
  ]);

  const checkDisabledUnsuitablePressByRoles = () => {
    if (isSuperAdmin) return false;
    return true;
  };

  const checkDisabledUnsuitablePressByStatus = (status: ProcessStatusCode) => {
    if (status !== DRAFT_STATUS_CODE.published) return false;
    return true;
  };

  const clickUnsuitableCheckbox = (
    pressId: number,
    status: ProcessStatusCode,
  ) => {
    if (status === DRAFT_STATUS_CODE.unsuitable) {
      turnOffUnsuitable(pressId);
      updateReviewRequiredStatus({
        pressId,
        requiredReviewInfo: {
          isReviewRequired: false,
          reviewId: reviewIdList || [],
          newInfoDetails: null,
        },
      });
    }
    if (status !== DRAFT_STATUS_CODE.unsuitable) {
      setTargetUnsuitablePressId(pressId);
      openModalUnsuitablePress();
    }
  };

  const handleCloseModal = () => {
    setTargetUnsuitablePressId(0);
    closeModalUnsuitablePress();
  };

  useEffect(() => {
    return () => {
      if (isOpenModalUnsuitablePress) {
        closeModalUnsuitablePress();
      }
    };
  }, [closeModalUnsuitablePress, isOpenModalUnsuitablePress]);

  return (
    <>
      {articleRejectReason && reviewRequiredText === "" && (
        <>
          {articleRejectReason}
          {!!duplArticleId && `\n(${duplArticleId})`}
        </>
      )}
      {reviewRequiredText && <>{reviewRequiredText}</>}
      {articleRejectReason === "" && reviewRequiredText === "" && "-"}
      <UnsuitableCheckboxIcon
        isChecked={draftArticleStatusHighest === "S930" || false}
        onClick={() => clickUnsuitableCheckbox(id, draftArticleStatusHighest)}
        disabled={
          checkDisabledUnsuitablePressByRoles() === true ||
          checkDisabledUnsuitablePressByStatus(draftArticleStatusHighest) ===
            true
        }
        draftArticleId={id}
        isOpenModalUnsuitablePress={
          targetUnsuitablePressId === id && !articleRejectReason
        }
        articleRejectReason={articleRejectReason}
        closeModalUnsuitablePress={handleCloseModal}
        mcEditorName={mcEditorName}
        aiDone={aiDone || false}
        openModalUnsuitablePress={openModalUnsuitablePress}
        unsuitableReasonUsageType={unsuitableReasonUsageType}
      />
    </>
  );
};

export default ListUnsuitableContainer;
